import type { TooltipProps } from '@mui/material'
// material-ui
import type { Theme } from '@mui/material/styles'
// type
import type { ColorProps } from 'types/extended'
import { Box, Tooltip as MuiTooltip } from '@mui/material'
import { styled } from '@mui/material/styles'

import { tooltipClasses } from '@mui/material/Tooltip'

// project import
import getColors from 'utils/getColors'

// ==============================|| TOOLTIP - VARIANT ||============================== //

interface TooltipStyleProps {
  color?: ColorProps | string
  labelColor?: ColorProps | string
  theme: Theme
}

function getVariantStyle({ color, theme, labelColor }: TooltipStyleProps) {
  const colors = getColors(theme, color as ColorProps)
  const { main, contrastText } = colors
  const colorValue = color || ''

  if (['primary', 'secondary', 'info', 'success', 'warning', 'error'].includes(colorValue)) {
    return {
      [`& .${tooltipClasses.tooltip}`]: {
        backgroundColor: main,
        color: labelColor || contrastText,
      },
      [`& .${tooltipClasses.arrow}`]: {
        color: main,
      },
    }
  } else {
    return {
      [`& .${tooltipClasses.tooltip}`]: {
        backgroundColor: colorValue,
        color: labelColor || contrastText,
        boxShadow: theme.shadows[1],
      },
      [`& .${tooltipClasses.arrow}`]: {
        color: colorValue,
      },
    }
  }
}

// ==============================|| STYLED - TOOLTIP COLOR ||============================== //

interface StyleProps {
  arrow: TooltipProps['arrow']
  labelColor?: ColorProps | string
  color?: ColorProps | string
}

const TooltipStyle = styled(MuiTooltip, {
  shouldForwardProp: (prop) => prop !== 'color' && prop !== 'labelColor',
})<StyleProps>(({ theme, color = 'primary', labelColor = 'default' }) => ({
  ...(color && getVariantStyle({ color, theme, labelColor })),
}))

// ==============================|| EXTENDED - TOOLTIP ||============================== //

interface Props extends TooltipProps {
  color?: ColorProps | string
  labelColor?: ColorProps | string
  arrow?: boolean
  children: TooltipProps['children']
}

export default function CustomTooltip({ children, arrow = true, labelColor = '', ...rest }: Props) {
  return (
    <Box display="flex">
      <TooltipStyle arrow={arrow} {...rest} labelColor={labelColor}>
        {children}
      </TooltipStyle>
    </Box>
  )
}
